import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SettingsProvider } from './context/SettingsContext';
import ProtectedRoute from './components/ProtectedRoute';
import Settings from './pages/Settings';
import Login from './pages/Login';
import Research from './pages/Research';
import RequestDetail from './pages/RequestDetail';
import Results from './pages/Results';
import Reminders from './pages/Reminders';
import TwoOneOne from './pages/TwoOneOne';
import PrepHistory from './pages/PrepHistory';
import Confirm from './pages/Confirm';
import PCTrader from './pages/PCTrader';
import Analytics from './pages/Analytics';
import PriceChecks from './pages/PriceChecks';
import PriceCheckDetail from './pages/PriceCheckDetail';
import Prompts from './pages/Prompts';
import PromptResults from './pages/PromptResults';
import HermesAgent from './pages/HermesAgent';
import Home from './pages/Home';
import Pulseboard from './pages/Pulseboard';

// Redirect authenticated users away from auth pages
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const AppRoutes = () => {
  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/confirm"
        element={<Confirm />}
      />
      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Home />
          </ProtectedRoute>
        }
      />
      <Route
        path="/research"
        element={
          <ProtectedRoute>
            <Research />
          </ProtectedRoute>
        }
      />
      <Route
        path="/results"
        element={
          <ProtectedRoute>
            <Results />
          </ProtectedRoute>
        }
      />
      <Route
        path="/requests/:id"
        element={
          <ProtectedRoute>
            <RequestDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reminders"
        element={
          <ProtectedRoute>
            <Reminders />
          </ProtectedRoute>
        }
      />

      <Route
        path="/pctrader"
        element={
          <ProtectedRoute>
            <PCTrader />
          </ProtectedRoute>
        }
      />
      <Route path="/211" element={<Navigate to="/ep" replace />} />
      <Route path="/211/history" element={<Navigate to="/ep/history" replace />} />
      <Route
        path="/ep"
        element={
          <ProtectedRoute>
            <TwoOneOne />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ep/history"
        element={
          <ProtectedRoute>
            <PrepHistory />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics"
        element={
          <ProtectedRoute>
            <Analytics />
          </ProtectedRoute>
        }
      />
      <Route
        path="/price-checks"
        element={
          <ProtectedRoute>
            <PriceChecks />
          </ProtectedRoute>
        }
      />
      <Route
        path="/price-checks/:id"
        element={
          <ProtectedRoute>
            <PriceCheckDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/prompts"
        element={
          <ProtectedRoute>
            <Prompts />
          </ProtectedRoute>
        }
      />
      <Route
        path="/prompts/:promptid/results"
        element={
          <ProtectedRoute>
            <PromptResults />
          </ProtectedRoute>
        }
      />
      <Route
        path="/hermes-agent"
        element={
          <ProtectedRoute>
            <HermesAgent />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pulseboard"
        element={
          <ProtectedRoute>
            <Pulseboard />
          </ProtectedRoute>
        }
      />

      {/* 404 */}
      <Route
        path="*"
        element={
          <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="text-center">
              <h1 className="text-4xl font-bold text-gray-900 mb-4">404</h1>
              <p className="text-gray-600 mb-4">Page not found</p>
              <a href="/" className="text-primary-600 hover:text-primary-700">
                Go to dashboard
              </a>
            </div>
          </div>
        }
      />
    </Routes>
  );
};

const AppShell = () => {
  const { user, isAuthenticated } = useAuth();
  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1">
        <AppRoutes />
      </div>
      {isAuthenticated && (
        <footer className="fixed bottom-0 left-0 right-0 border-t border-gray-200 bg-white py-3 px-4">
          <div className="text-sm text-gray-400 text-center">
            Logged in as <span className="font-medium text-gray-500">{user?.name || user?.email}</span>
          </div>
        </footer>
      )}
    </div>
  );
};

const App = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SettingsProvider>
          <AppShell />
        </SettingsProvider>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default App;
